import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { validateBookText } from '../utils/textValidator';

interface LibrarySchema extends DBSchema {
    gutendex_cache: {
        key: string;
        value: {
            id: string; // "query|lang|page"
            ts: number;
            responseJson: any;
        };
    };
    library_books: {
        key: string; // gutendex id string
        value: {
            id: string;
            title: string;
            authors: { name: string, birth_year?: number, death_year?: number }[];
            languages: string[];
            coverUrl?: string;
            formats: Record<string, string>; // mime -> url
            addedAt: number;
            preferredFormatUrl?: string;
            status: 'metadata-only' | 'downloading' | 'ready' | 'error';
            textContent?: string; // For the text content
            chunkedChapters?: string[]; // Cached chapters
            errorMessage?: string;
            ingestDetails?: {
                source: string;
                format: string;
                fallback?: boolean;
                shortReason?: string;
                timestamp: number;
            };
            isBookClub?: boolean; // Added in v3
            audioDownloadState?: 'not_downloaded' | 'downloading' | 'partial' | 'downloaded_verified' | 'failed';
            audioDownloadProgress?: number;
            audioVerifiedTimestamp?: number;
            source?: string; // e.g. "gutendex" or "book-club"
            gutenbergId?: string;
        };
    };
    book_assets: {
        key: [string, string]; // [bookId, assetKind]
        value: {
            bookId: string;
            assetKind: 'text' | 'html' | 'epub';
            contentType: string;
            text?: string;
            blob?: Blob;
            byteLength: number;
            ts: number;
        };
    };
    global_manifest: { // Added in v3
        key: string; // "main"
        value: {
            id: string;
            items: any[];
            ts: number;
        };
    };
    audio_resume: { // Added in v4
        key: string; // bookId
        value: {
            bookId: string;
            trackIndex: number;
            positionSeconds: number;
            updatedAt: number;
            durationSeconds?: number;
        };
    };
}

const DB_NAME = 'corpora_library_v1'; // Logic handled in upgrade

class LibraryDBService {
    private dbPromise: Promise<IDBPDatabase<LibrarySchema>>;

    constructor() {
        this.dbPromise = openDB<LibrarySchema>(DB_NAME, 4, { // Bumped version to 4
            upgrade(db, oldVersion, newVersion, transaction) {
                if (oldVersion < 1) {
                    db.createObjectStore('gutendex_cache', { keyPath: 'id' });
                    db.createObjectStore('library_books', { keyPath: 'id' });
                }
                if (oldVersion < 2) {
                    // v2 added book_assets
                    if (!db.objectStoreNames.contains('book_assets')) {
                        db.createObjectStore('book_assets', { keyPath: ['bookId', 'assetKind'] });
                    }
                }
                if (oldVersion < 3) {
                    // v3 adds global_manifest
                    if (!db.objectStoreNames.contains('global_manifest')) {
                        db.createObjectStore('global_manifest', { keyPath: 'id' });
                    }
                }
                if (oldVersion < 4) {
                    // v4 adds audio_resume
                    if (!db.objectStoreNames.contains('audio_resume')) {
                        db.createObjectStore('audio_resume', { keyPath: 'bookId' });
                    }
                }
            },
        });
    }

    async getCache(query: string, lang: string, page: number) {
        const key = `${query}|${lang}|${page}`;
        const db = await this.dbPromise;
        const entry = await db.get('gutendex_cache', key);
        if (!entry) return null;

        // TTL 24h
        if (Date.now() - entry.ts > 24 * 60 * 60 * 1000) {
            await db.delete('gutendex_cache', key);
            return null;
        }
        return entry.responseJson;
    }

    async setCache(query: string, lang: string, page: number, data: any) {
        const key = `${query}|${lang}|${page}`;
        const db = await this.dbPromise;
        await db.put('gutendex_cache', {
            id: key,
            ts: Date.now(),
            responseJson: data
        });
    }

    async addBook(book: LibrarySchema['library_books']['value']) {
        if (book.status === 'ready' && book.textContent) {
            validateBookText(book.textContent);
        }
        const db = await this.dbPromise;
        await db.put('library_books', book);
    }

    async getBook(id: string) {
        const db = await this.dbPromise;
        return await db.get('library_books', id);
    }

    async getAllBooks() {
        const db = await this.dbPromise;
        return await db.getAll('library_books');
    }

    async updateBookStatus(id: string, status: LibrarySchema['library_books']['value']['status'], textContent?: string, chunkedChapters?: string[], ingestDetails?: LibrarySchema['library_books']['value']['ingestDetails']) {
        if (status === 'ready' && textContent !== undefined) {
            validateBookText(textContent);
        }
        const db = await this.dbPromise;
        const book = await db.get('library_books', id);
        if (book) {
            book.status = status;
            if (textContent !== undefined) book.textContent = textContent;
            if (chunkedChapters !== undefined) book.chunkedChapters = chunkedChapters;
            if (ingestDetails !== undefined) book.ingestDetails = ingestDetails;
            await db.put('library_books', book);
        }
    }

    async updateAudioDownloadFields(
        id: string,
        fields: {
            audioDownloadState?: 'not_downloaded' | 'downloading' | 'partial' | 'downloaded_verified' | 'failed';
            audioDownloadProgress?: number;
            audioVerifiedTimestamp?: number;
        }
    ) {
        const db = await this.dbPromise;
        const book = await db.get('library_books', id);
        if (book) {
            if (fields.audioDownloadState !== undefined) book.audioDownloadState = fields.audioDownloadState;
            if (fields.audioDownloadProgress !== undefined) book.audioDownloadProgress = fields.audioDownloadProgress;
            if (fields.audioVerifiedTimestamp !== undefined) book.audioVerifiedTimestamp = fields.audioVerifiedTimestamp;
            await db.put('library_books', book);
        }
    }

    async saveBookAsset(bookId: string, text: string) {
        validateBookText(text);
        const db = await this.dbPromise;
        // Check if existing to preserve TS? No, simple overwrite is fine.
        await db.put('book_assets', {
            bookId,
            assetKind: 'text',
            contentType: 'text/plain',
            text,
            byteLength: text.length,
            ts: Date.now()
        });
    }

    async getBookAsset(bookId: string) {
        const db = await this.dbPromise;
        return await db.get('book_assets', [bookId, 'text']);
    }

    async deleteBook(id: string) {
        const db = await this.dbPromise;
        const tx = db.transaction(['library_books', 'book_assets'], 'readwrite');

        // 1. Delete metadata
        await tx.objectStore('library_books').delete(id);

        // 2. Delete all assets for this bookId
        // explicit delete for known types
        await tx.objectStore('book_assets').delete([id, 'text']);
        await tx.objectStore('book_assets').delete([id, 'html']);
        await tx.objectStore('book_assets').delete([id, 'epub']);

        await tx.done;
    }

    async deleteBookAsset(bookId: string) {
        const db = await this.dbPromise;
        await db.delete('book_assets', [bookId, 'text']);
    }

    // --- V3 Manifest Methods ---

    async saveAudioManifest(items: any[]) {
        const db = await this.dbPromise;

        // 1. Slim Down - specific allowed keys only
        const slimItems = items.map(item => ({
            id: item.id,
            title: item.title,
            author: item.author,
            coverUrl: item.coverUrl,
            duration: item.duration, // if available
            isBookClub: !!item.isBookClub,
            hasAudio: true, // Implicit if in this list
            trackCount: Array.isArray(item.tracks) ? item.tracks.length : 0,
            // Explicitly EXCLUDE tracks, chapters, playlists to save space
        }));

        // 2. Safety Size Check
        try {
            const json = JSON.stringify(slimItems);
            const sizeMB = new Blob([json]).size / (1024 * 1024);
            if (sizeMB > 2) {
                console.warn(`[LibraryDB] Global Manifest is large (${sizeMB.toFixed(2)} MB). Truncating extra fields if needed.`);
                // For now, just warning. The slim mapping above is the primary guard.
            }
        } catch (e) {
            console.warn("Failed to size-check manifest", e);
        }

        // We only store one manifest "main"
        await db.put('global_manifest', {
            id: 'main',
            items: slimItems, // Store structured array
            ts: Date.now()
        });
    }

    async getAudioManifest() {
        const db = await this.dbPromise;
        const entry = await db.get('global_manifest', 'main');
        if (!entry) return null;
        return entry.items;
    }

    // --- V4 Audio Resume Methods ---

    async getAudioResume(bookId: string) {
        const db = await this.dbPromise;
        return await db.get('audio_resume', bookId);
    }

    async saveAudioResume(data: LibrarySchema['audio_resume']['value']) {
        const db = await this.dbPromise;
        await db.put('audio_resume', data);
    }

    async deleteAudioResume(bookId: string) {
        const db = await this.dbPromise;
        await db.delete('audio_resume', bookId);
    }
}

export const LibraryDB = new LibraryDBService();
